import { LeadPerformanceTab } from "@/components/crm/LeadPerformanceTab";
import { SelfBookedLeadsTab } from "@/components/crm/SelfBookedLeadsTab";
import { LeadFrtTab } from "@/components/crm/LeadFrtTab";
import { LeadTalkTimeTab } from "@/components/crm/LeadTalkTimeTab";
import { LeadOpenAgeingTab } from "@/components/crm/LeadOpenAgeingTab";
import { LeadOpdReportTab } from "@/components/crm/LeadOpdReportTab";
import { LeadSarvamOpdPerformanceTab } from "@/components/crm/LeadSarvamOpdPerformanceTab";
import { OrganicLeadConversionsTab } from "@/components/crm/OrganicLeadConversionsTab";
import { LeadBantScoringTab } from "@/components/crm/LeadBantScoringTab";
import { CampaignRecommendationTab } from "@/components/crm/CampaignRecommendationTab";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { leadsApi } from "@/api/leads";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
  CartesianGrid,
} from "recharts";
import { LEAD_STATUS_OPTIONS, formatLeadStatusLabel, formatLeadSubStatusLabel } from "@/lib/crmLeadStyles";
import type { LeadStatus, LeadSubStatus } from "@/api/leads";
import { isCrmViewOnlyUser } from "@/utils/crmAccess";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { useChainReportHospitalScope } from "@/hooks/useChainReportHospitalScope";

const COLORS = ["#2563eb", "#16a34a", "#dc2626", "#9333ea", "#ea580c", "#0891b2"];

/** Hex colors aligned with CRM status badges (Recharts) */
const STAGE_CHART_COLORS: Record<LeadStatus, string> = {
  NEW: "#0284c7",
  CONTACTED: "#d97706",
  INTERESTED: "#0891b2",
  FOLLOW_UP: "#7c3aed",
  CONVERTED: "#059669",
  LOST: "#64748b",
  CLOSED: "#4d7c0f",
};

function priorityBarColor(label: string | undefined): string {
  const u = String(label || "").toUpperCase();
  if (u === "HIGH") return "#059669";
  if (u === "LOW") return "#dc2626";
  return "#ca8a04";
}

function enumerateDaysInclusive(fromYmd: string, toYmd: string): string[] {
  const parse = (s: string) => {
    const [y, mo, da] = s.split("-").map(Number);
    return new Date(y, mo - 1, da);
  };
  const a = parse(fromYmd);
  const b = parse(toYmd);
  const keys: string[] = [];
  for (let cur = new Date(a); cur <= b; cur.setDate(cur.getDate() + 1)) {
    const y = cur.getFullYear();
    const mo = String(cur.getMonth() + 1).padStart(2, "0");
    const da = String(cur.getDate()).padStart(2, "0");
    keys.push(`${y}-${mo}-${da}`);
  }
  return keys;
}

function parseYmd(ymd: string): Date {
  const [y, mo, da] = ymd.split("-").map(Number);
  return new Date(y, mo - 1, da);
}

function ymdFromDate(d: Date): string {
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${da}`;
}

/** Week bucket key (Monday start) in YYYY-MM-DD form. */
function startOfWeekKey(dayYmd: string): string {
  const dt = parseYmd(dayYmd);
  const jsDay = dt.getDay(); // 0=Sun..6=Sat
  const mondayBased = jsDay === 0 ? 7 : jsDay; // 1=Mon..7=Sun
  dt.setDate(dt.getDate() - (mondayBased - 1));
  return ymdFromDate(dt);
}

function monthKey(dayYmd: string): string {
  return dayYmd.slice(0, 7);
}

function fmtDays(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  return `${n < 10 ? n.toFixed(1) : Math.round(n)} d`;
}

function subStatusTableLabel(key: string): string {
  if (key === "__NONE__") return "Not specified";
  return formatLeadSubStatusLabel(key as LeadSubStatus);
}

function statusKeyFromSubStatus(sub: string): LeadStatus | "__UNKNOWN__" {
  const s = String(sub || "").toUpperCase();
  const byStatus: Array<[LeadStatus, string[]]> = [
    ["NEW", ["NOT_CONTACTED", "AUTO_GENERATED", "WALK_IN_ENQUIRY", "REFERRED_LEAD"]],
    ["CONTACTED", ["CONNECTED_DETAILS_SHARED", "CONNECTED_NEEDS_FOLLOW_UP", "NOT_REACHABLE", "SWITCHED_OFF"]],
    ["INTERESTED", ["ASKED_FOR_COST", "ASKED_FOR_DOCTOR_INFO", "ASKED_FOR_APPOINTMENT", "WANTS_CALL_BACK", "COMPARING_OPTIONS"]],
    ["FOLLOW_UP", ["FOLLOW_UP_SCHEDULED", "AWAITING_PATIENT_DECISION", "AWAITING_REPORTS", "AWAITING_FAMILY_DISCUSSION", "RESCHEDULED_CALL", "NO_RESPONSE_AFTER_FOLLOW_UP"]],
    ["CONVERTED", ["OPD_BOOKED", "OPD_VISITED", "IPD_ADMITTED", "PROCEDURE_BOOKED", "DAY_CARE_DONE"]],
    ["LOST", ["PRICE_TOO_HIGH", "WENT_TO_COMPETITOR", "NOT_INTERESTED", "TREATMENT_POSTPONED", "LOCATION_ISSUE", "TRUST_ISSUE", "NO_RESPONSE", "DUPLICATE_LEAD", "WRONG_NUMBER"]],
    ["CLOSED", ["SUCCESSFULLY_TREATED", "CASE_COMPLETED", "LONG_TERM_FOLLOW_UP"]],
  ];
  for (const [st, subs] of byStatus) {
    if (subs.includes(s)) return st;
  }
  if (s === "__NONE__" || s === "NULL" || s === "UNSET" || s === "NONE" || s === "" || s === "UNDEFINED") return "__UNKNOWN__";
  return "__UNKNOWN__";
}

function mapObjToRows(obj: Record<string, number>) {
  return Object.entries(obj || {}).map(([name, value]) => ({ name, value }));
}

type Tab = "overview" | "transitions" | "lost" | "revenue" | "benchmark" | "opd-outcomes" | "opd-report" | "sarvam-opd-performance" | "organic-conversions" | "self-booked" | "performance" | "talk-time" | "frt" | "ageing" | "bant-scoring" | "campaign-recommendations";

function formatOpdOutcomeLabel(outcome: string): string {
  switch (outcome) {
    case "opd_visited_pending":
      return "OPD Visited (pending)";
    case "lost":
      return "Lost";
    case "closed_long_term_follow_up":
      return "Closed (long-term follow-up)";
    case "ipd":
      return "IPD";
    default:
      return outcome;
  }
}

export default function LeadInsightsPage() {
  const { user } = useAuth();
  const crmViewOnly = isCrmViewOnlyUser(user);
  const [from, setFrom] = useState(() =>
    new Date(Date.now() - 1000 * 60 * 60 * 24 * 30).toISOString().slice(0, 10)
  );
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));
  const {
    showHospitalFilter,
    hospitalOptions,
    selectedHospitalId,
    setSelectedHospitalId,
    reportHospitalId,
  } = useChainReportHospitalScope({ defaultScope: "consolidated" });
  const [tab, setTab] = useState<Tab>("overview");
  const [downloadingTransitionEdge, setDownloadingTransitionEdge] = useState<string | null>(null);

  const reportParams = useMemo(() => {
    const p: { from: string; to: string; hospitalId?: string } = { from, to };
    if (reportHospitalId) p.hospitalId = reportHospitalId;
    return p;
  }, [from, to, reportHospitalId]);

  const movementQuery = useQuery({
    queryKey: ["lead-movement-report", reportParams, user?.role],
    queryFn: async () => {
      const res = await leadsApi.getMovementReport(reportParams);
      return res.data.data;
    },
  });

  const lostQuery = useQuery({
    queryKey: ["lead-lost-report", reportParams, user?.role],
    queryFn: async () => {
      const res = await leadsApi.getLostReasonsReport(reportParams);
      return res.data.data;
    },
  });

  const opdOutcomeQuery = useQuery({
    queryKey: ["lead-opd-outcome-report", reportParams, user?.role],
    queryFn: async () => {
      const res = await leadsApi.getOpdVisitedOutcomeReport(reportParams);
      return res.data.data;
    },
    enabled: tab === "opd-outcomes",
  });

  const transitionQuery = useQuery({
    queryKey: ["lead-transitions", reportParams, user?.role],
    queryFn: async () => {
      const res = await leadsApi.getTransitionReport(reportParams);
      return res.data.data;
    },
    enabled: tab === "transitions" || tab === "overview",
  });

  const benchmarkQuery = useQuery({
    queryKey: ["lead-benchmark", from, to],
    queryFn: async () => {
      const res = await leadsApi.getBenchmarkReport({ from, to });
      return res.data.data;
    },
    enabled: user?.role === "SUPER_ADMIN" && tab === "benchmark",
  });

  const canSeeCrmRevenue = user?.role === "SUPER_ADMIN" || user?.role === "HOSPITAL_ADMIN";
  const canSeeOpdReport =
    user?.role === "SUPER_ADMIN" ||
    user?.role === "HOSPITAL_ADMIN" ||
    user?.role === "SALES_MANAGER";
  const crmRevenueHospitalReady =
    !!reportHospitalId && reportHospitalId !== "all";
  const crmRevenueQuery = useQuery({
    queryKey: ["lead-crm-revenue", reportParams, user?.role],
    queryFn: async () => (await leadsApi.getCrmLeadRevenueReport(reportParams)).data.data,
    enabled: canSeeCrmRevenue && tab === "revenue" && crmRevenueHospitalReady,
  });

  const statusRows = useMemo(() => mapObjToRows(movementQuery.data?.byStatus || {}), [movementQuery.data]);
  const sourceRows = useMemo(() => mapObjToRows(movementQuery.data?.bySource || {}), [movementQuery.data]);
  const lostRows = useMemo(() => mapObjToRows(lostQuery.data?.byCategory || {}), [lostQuery.data]);

  const timeBuckets = useMemo(() => {
    const byDaySource: Record<string, Record<string, number>> =
      (movementQuery.data?.byDaySource as any) || {};
    const byDayStatus: Record<string, Record<string, number>> =
      (movementQuery.data?.byDayStatus as any) || {};
    const byDaySubStatus: Record<string, Record<string, number>> =
      (movementQuery.data?.byDaySubStatus as any) || {};

    const dayKeys = enumerateDaysInclusive(from, to);
    const toDt = parseYmd(to);
    const last7Start = new Date(toDt);
    last7Start.setDate(last7Start.getDate() - 6);
    const fromDt = parseYmd(from);
    if (last7Start.getTime() < fromDt.getTime()) {
      // If the selected range is shorter than 7 days, clamp to `from`.
      last7Start.setTime(fromDt.getTime());
    }

    const last7Keys = enumerateDaysInclusive(ymdFromDate(last7Start), to);

    const dayTotals: Record<string, number> = {};
    const sourceTotals: Record<string, number> = {};
    for (const d of dayKeys) {
      const row = byDaySource[d] || {};
      const total = Object.values(row).reduce((s, n) => s + (n || 0), 0);
      dayTotals[d] = total;
      for (const [src, cnt] of Object.entries(row)) {
        sourceTotals[src] = (sourceTotals[src] || 0) + (cnt || 0);
      }
    }

    const sortedSources = Object.keys(sourceTotals).sort(
      (a, b) => (sourceTotals[b] || 0) - (sourceTotals[a] || 0)
    );
    // Show all sources to match "source wise lead count" requirement.
    const topSources = sortedSources;
    const otherSources: string[] = [];

    const subTotals: Record<string, number> = {};
    for (const d of dayKeys) {
      const row = byDaySubStatus[d] || {};
      for (const [sub, cnt] of Object.entries(row)) {
        subTotals[sub] = (subTotals[sub] || 0) + (cnt || 0);
      }
    }
    const topSubStatuses = Object.keys(subTotals).sort(
      (a, b) => (subTotals[b] || 0) - (subTotals[a] || 0)
    );
    const otherSubStatuses: string[] = [];

    const weekToDays: Record<string, string[]> = {};
    const monthToDays: Record<string, string[]> = {};
    for (const d of dayKeys) {
      const wk = startOfWeekKey(d);
      if (!weekToDays[wk]) weekToDays[wk] = [];
      weekToDays[wk].push(d);

      const mk = monthKey(d);
      if (!monthToDays[mk]) monthToDays[mk] = [];
      monthToDays[mk].push(d);
    }

    const weekKeys = Object.keys(weekToDays).sort(
      (a, b) => parseYmd(a).getTime() - parseYmd(b).getTime()
    );
    const monthKeys = Object.keys(monthToDays).sort(
      (a, b) => parseYmd(`${a}-01`).getTime() - parseYmd(`${b}-01`).getTime()
    );

    return {
      dayKeys,
      last7Keys,
      weekKeys,
      monthKeys,
      weekToDays,
      monthToDays,
      byDaySource,
      byDayStatus,
      byDaySubStatus,
      byDaySubStatusByStatus: (() => {
        const out: Record<string, Record<string, Record<string, number>>> = {};
        for (const d of dayKeys) {
          const row = byDaySubStatus[d] || {};
          const bucket: Record<string, Record<string, number>> = {};
          for (const [sub, cnt] of Object.entries(row)) {
            const st = statusKeyFromSubStatus(sub);
            const stKey = st === "__UNKNOWN__" ? "__UNKNOWN__" : st;
            if (!bucket[stKey]) bucket[stKey] = {};
            bucket[stKey][sub] = (bucket[stKey][sub] || 0) + (cnt || 0);
          }
          out[d] = bucket;
        }
        return out;
      })(),
      dayTotals,
      topSources,
      otherSources,
      topSubStatuses,
      otherSubStatuses,
    };
  }, [from, to, movementQuery.data]);

  const transitionEdgeRows = useMemo(
    () => mapObjToRows(transitionQuery.data?.transitions || {}),
    [transitionQuery.data]
  );
  const byDayRows = useMemo(() => {
    const raw = transitionQuery.data?.byDay || {};
    const keys = enumerateDaysInclusive(from, to);
    return keys.map((name) => ({ name, value: raw[name] ?? 0 }));
  }, [transitionQuery.data, from, to]);

  const stackedBySource = useMemo(() => {
    const bySourceByStatus = (movementQuery.data?.bySourceByStatus || {}) as Record<
      string,
      Record<string, number>
    >;
    const sources = Object.keys(bySourceByStatus).sort((a, b) => a.localeCompare(b));
    return sources.map((source) => {
      const row: Record<string, string | number> = {
        source: source.length > 16 ? `${source.slice(0, 14)}…` : source,
        sourceFull: source,
      };
      for (const st of LEAD_STATUS_OPTIONS) {
        row[st] = bySourceByStatus[source]?.[st] ?? 0;
      }
      return row;
    });
  }, [movementQuery.data]);

  const priorityRows = useMemo(
    () => mapObjToRows(movementQuery.data?.byPriority || {}),
    [movementQuery.data]
  );

  const ownerRows = useMemo(() => {
    const rows = mapObjToRows(movementQuery.data?.byOwner || {});
    return rows.sort((a, b) => b.value - a.value).slice(0, 12);
  }, [movementQuery.data]);

  const conv = movementQuery.data?.conversionTime;
  const overallConvRate =
    conv?.cohortSize && conv.cohortSize > 0 && conv.sampleSize != null
      ? Math.round((conv.sampleSize / conv.cohortSize) * 1000) / 10
      : null;

  const benchmarkRows = useMemo(() => {
    const bh = benchmarkQuery.data?.byHospital as
      | Record<string, { name: string; total: number; byStatus: Record<string, number> }>
      | undefined;
    if (!bh) return [];
    return Object.entries(bh).map(([id, v]) => ({
      id,
      name: v.name,
      total: v.total,
      converted: v.byStatus?.CONVERTED ?? 0,
      lost: v.byStatus?.LOST ?? 0,
    }));
  }, [benchmarkQuery.data]);

  const downloadDump = async () => {
    const res = await leadsApi.downloadLeadDump({
      hospitalId:
        reportHospitalId && reportHospitalId !== "all" ? reportHospitalId : undefined,
    });
    const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads-dump-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const downloadLostCsv = async () => {
    const res = await leadsApi.downloadLostReasonsCsv({
      from,
      to,
      ...(reportHospitalId ? { hospitalId: reportHospitalId } : {}),
    });
    const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `lost-leads-${from}-to-${to}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const downloadCrmRevenueCsv = async () => {
    const res = await leadsApi.downloadCrmLeadRevenueDump(reportParams);
    const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `crm-revenue-${from}-to-${to}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const downloadOpdOutcomeCsv = async () => {
    const res = await leadsApi.downloadOpdVisitedOutcomeReport(reportParams);
    const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `opd-visited-outcomes-${from}-to-${to}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const downloadTransitionCsv = async (edge: string) => {
    if (crmViewOnly) return;
    setDownloadingTransitionEdge(edge);
    try {
      const res = await leadsApi.downloadTransitionReport({ ...reportParams, edge });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = edge.replace(/[^A-Za-z0-9_-]+/g, "_");
      a.download = `lead-transitions-${safe}-${from}-to-${to}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: unknown) {
      const msg =
        e && typeof e === "object" && "response" in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      toast.error(msg || "Failed to download transition CSV");
    } finally {
      setDownloadingTransitionEdge(null);
    }
  };

  return (
    <div className="p-6 space-y-4 max-w-[1400px] mx-auto">
      <div>
        <h1 className="text-2xl font-semibold">Lead Insights & AI Reports</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Configure <code className="text-xs bg-muted px-1 rounded">OPENAI_API_KEY</code> on the
          server for LLM-assisted priority and lost-reason analysis; otherwise rules apply.
        </p>
        <div className="flex flex-wrap gap-2 mt-3">
          {(user?.role === "HOSPITAL_ADMIN" || user?.role === "SUPER_ADMIN") && (
            <>
              <Button variant="outline" size="sm" onClick={() => downloadDump()}>
                Download lead dump (CSV)
              </Button>
              <Button variant="outline" size="sm" onClick={() => downloadLostCsv()}>
                Export lost leads (CSV)
              </Button>
            </>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs text-muted-foreground">From</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          {showHospitalFilter && (
            <div>
              <Label className="text-xs text-muted-foreground">Hospital scope</Label>
              <Select
                value={selectedHospitalId || "all"}
                onValueChange={(v) => setSelectedHospitalId(v === "all" ? "all" : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All hospitals" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {user?.adminScope === "CHAIN" ? "Consolidated (all chain hospitals)" : "All hospitals"}
                  </SelectItem>
                  {hospitalOptions.map((h) => (
                    <SelectItem key={h.id} value={h.id}>
                      {h.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex items-end text-sm text-muted-foreground md:col-span-1">
            Leads in range: {movementQuery.data?.total ?? 0} · Lost: {lostQuery.data?.totalLost ?? 0}
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-2 border-b pb-2">
        {(
          [
            ["overview", "Overview"],
            ["performance", "Lead Performance"],
            ["campaign-recommendations", "Campaign recommendations"],
            ["talk-time", "Talk time"],
            ["frt", "FRT"],
            ["ageing", "Open lead ageing"],
            ...(canSeeOpdReport ? ([["opd-report", "OPD report"]] as const) : []),
            ...(canSeeOpdReport
              ? ([["sarvam-opd-performance", "Sarvam OPD performance"]] as const)
              : []),
            ...(canSeeOpdReport ? ([["organic-conversions", "Organic conversions"]] as const) : []),
            ["opd-outcomes", "OPD visited outcomes"],
            ["self-booked", "Self booked"],
            ["transitions", "Status transitions"],
            ["lost", "Lost drilldown"],
            ...(user?.role === "HOSPITAL_ADMIN" || user?.role === "SUPER_ADMIN"
              ? ([["bant-scoring", "Lead Scoring Performance"]] as const)
              : []),
            ...(canSeeCrmRevenue ? ([["revenue", "CRM revenue"]] as const) : []),
            ...(user?.role === "SUPER_ADMIN" ? ([["benchmark", "Hospital benchmark"]] as const) : []),
          ] as const
        ).map(([k, label]) => (
          <Button
            key={k}
            variant={tab === k ? "default" : "outline"}
            size="sm"
            onClick={() => setTab(k as Tab)}
          >
            {label}
          </Button>
        ))}
      </div>

      {tab === "revenue" && canSeeCrmRevenue && (
        <div className="space-y-4">
          {!crmRevenueHospitalReady && user?.role === "SUPER_ADMIN" && (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200/80 rounded-md px-3 py-2">
              Pick a hospital to load CRM revenue and download the CSV for that hospital and the selected date range.
            </p>
          )}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              ["Total", crmRevenueQuery.data?.summary?.total],
              ["OPD", crmRevenueQuery.data?.summary?.opd],
              ["Lab", crmRevenueQuery.data?.summary?.lab],
              ["Pharma", crmRevenueQuery.data?.summary?.pharma],
              ["IPD", crmRevenueQuery.data?.summary?.ipd],
            ].map(([label, val]) => (
              <Card key={label as string} className="border-slate-200/80 shadow-sm bg-gradient-to-b from-white to-slate-50/40">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">{label as string}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-semibold">₹{Number(val || 0).toLocaleString("en-IN")}</p>
                  {label === "Total" && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {crmRevenueQuery.isLoading
                        ? "Loading…"
                        : crmRevenueQuery.data?.totalRows != null
                          ? `${crmRevenueQuery.data.totalRows} lead(s) with revenue in range · table shows top ${crmRevenueQuery.data.tablePreviewLimit ?? 500}`
                          : "Selected date range"}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="border-slate-200/80 shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-base">CRM lead-wise revenue</CardTitle>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={downloadCrmRevenueCsv}
                  disabled={crmRevenueQuery.isLoading || !crmRevenueHospitalReady}
                >
                  Download CSV (full list, date range)
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                OPD/Lab/Pharma from OPD bills (appointment-linked) by bill date. IPD from discharged admissions by discharge
                date, using IPD bill net amount including draft bills. Shows lead owner, source, and campaign. The CSV includes
                every matching lead for the selected range (not limited to the table preview).
              </p>
            </CardHeader>
            <CardContent>
              <div className="overflow-auto rounded-lg border">
                <table className="min-w-[1200px] w-full text-sm">
                  <thead className="bg-slate-50/70">
                    <tr className="border-b">
                      <th className="text-left p-3 font-semibold text-slate-700">Lead</th>
                      <th className="text-left p-3 font-semibold text-slate-700">Owner</th>
                      <th className="text-left p-3 font-semibold text-slate-700">Source</th>
                      <th className="text-left p-3 font-semibold text-slate-700">Campaign</th>
                      <th className="text-right p-3 font-semibold text-slate-700">OPD</th>
                      <th className="text-right p-3 font-semibold text-slate-700">Lab</th>
                      <th className="text-right p-3 font-semibold text-slate-700">Pharma</th>
                      <th className="text-right p-3 font-semibold text-slate-700">IPD</th>
                      <th className="text-right p-3 font-semibold text-slate-700">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(crmRevenueQuery.data?.items ?? []).map((r: any) => (
                      <tr key={r.leadId} className="border-b hover:bg-slate-50/50">
                        <td className="p-3">
                          <div className="font-medium text-slate-900">{r.patientName || "—"}</div>
                          <div className="text-xs text-muted-foreground font-mono">{r.phone || "—"}</div>
                        </td>
                        <td className="p-3 text-slate-700">{r.assignedToName || "Unassigned"}</td>
                        <td className="p-3 text-slate-700 max-w-[140px] truncate" title={r.source || ""}>
                          {r.source || "—"}
                        </td>
                        <td className="p-3 text-slate-700 max-w-[160px] truncate" title={r.campaignName || ""}>
                          {r.campaignName?.trim() ? r.campaignName : "—"}
                        </td>
                        <td className="p-3 text-right tabular-nums">₹{Number(r.opd || 0).toLocaleString("en-IN")}</td>
                        <td className="p-3 text-right tabular-nums">₹{Number(r.lab || 0).toLocaleString("en-IN")}</td>
                        <td className="p-3 text-right tabular-nums">₹{Number(r.pharma || 0).toLocaleString("en-IN")}</td>
                        <td className="p-3 text-right tabular-nums">₹{Number(r.ipd || 0).toLocaleString("en-IN")}</td>
                        <td className="p-3 text-right tabular-nums font-semibold text-slate-900">
                          ₹{Number(r.total || 0).toLocaleString("en-IN")}
                        </td>
                      </tr>
                    ))}
                    {(crmRevenueQuery.data?.items ?? []).length === 0 ? (
                      <tr>
                        <td colSpan={9} className="p-6 text-center text-muted-foreground">
                          {crmRevenueQuery.isLoading ? "Loading…" : "No CRM-attributed revenue in this range."}
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {tab === "overview" && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <Card className="border-slate-200/80 shadow-sm bg-gradient-to-b from-white to-slate-50/40">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Median time to convert</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-semibold">{fmtDays(conv?.medianDays)}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {conv?.sampleSize ?? 0} of {conv?.cohortSize ?? 0} leads converted (cohort)
                </p>
              </CardContent>
            </Card>
            <Card className="border-slate-200/80 shadow-sm bg-gradient-to-b from-white to-slate-50/40">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Avg. time to convert</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-semibold">{fmtDays(conv?.meanDays)}</p>
                <p className="text-xs text-muted-foreground mt-1">P90: {fmtDays(conv?.p90Days)}</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200/80 shadow-sm bg-gradient-to-b from-white to-slate-50/40">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Cohort conversion rate</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-semibold">
                  {overallConvRate != null ? `${overallConvRate}%` : "—"}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Leads created in date range</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200/80 shadow-sm bg-gradient-to-b from-white to-slate-50/40">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Open pipeline</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-semibold">{movementQuery.data?.funnel?.open ?? 0}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Median age {fmtDays(movementQuery.data?.openPipeline?.medianAgeDays)}
                </p>
              </CardContent>
            </Card>
            <Card className="border-slate-200/80 shadow-sm bg-gradient-to-b from-white to-slate-50/40">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Converted</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-semibold text-emerald-700">
                  {movementQuery.data?.funnel?.converted ?? 0}
                </p>
                <p className="text-xs text-muted-foreground mt-1">In cohort (current status)</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200/80 shadow-sm bg-gradient-to-b from-white to-slate-50/40">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Lost</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-semibold text-red-700">{movementQuery.data?.funnel?.lost ?? 0}</p>
                <p className="text-xs text-muted-foreground mt-1">In cohort (current status)</p>
              </CardContent>
            </Card>
          </div>

          <Card className="border-slate-200/70 shadow-md overflow-hidden bg-gradient-to-br from-white via-slate-50/40 to-white">
            <CardHeader>
              <CardTitle>Source × stage (leads created in range)</CardTitle>
              <p className="text-sm text-muted-foreground">
                Stacked counts by acquisition source and current pipeline stage — includes all statuses.
              </p>
            </CardHeader>
            <CardContent className="h-[360px]">
              {stackedBySource.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No leads in this period.</p>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={stackedBySource}
                    margin={{ bottom: 48, left: 4, right: 8 }}
                    maxBarSize={56}
                    barCategoryGap="14%"
                  >
                    <defs>
                      {LEAD_STATUS_OPTIONS.map((st) => (
                        <linearGradient key={st} id={`stageGrad-${st}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={STAGE_CHART_COLORS[st]} stopOpacity={1} />
                          <stop offset="100%" stopColor={STAGE_CHART_COLORS[st]} stopOpacity={0.65} />
                        </linearGradient>
                      ))}
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="source" tick={{ fontSize: 10 }} interval={0} angle={-22} textAnchor="end" height={70} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip
                      formatter={(value: number, name: string) => [value, formatLeadStatusLabel(name)]}
                      labelFormatter={(_, payload) =>
                        (payload?.[0]?.payload?.sourceFull as string) || String(payload?.[0]?.name ?? "")
                      }
                      contentStyle={{ borderRadius: 8, borderColor: "#e2e8f0" }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {LEAD_STATUS_OPTIONS.map((st) => (
                      <Bar
                        key={st}
                        dataKey={st}
                        stackId="stage"
                        fill={`url(#stageGrad-${st})`}
                        name={formatLeadStatusLabel(st)}
                        radius={[4, 4, 0, 0]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="border-slate-200/70 shadow-md bg-gradient-to-b from-white to-slate-50/30">
              <CardHeader>
                <CardTitle>Status (leads created in range)</CardTitle>
              </CardHeader>
              <CardContent className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={statusRows} maxBarSize={64} barCategoryGap="18%">
                    <defs>
                      <linearGradient id="statusBarGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3b82f6" />
                        <stop offset="100%" stopColor="#1e3a8a" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 10 }}
                      tickFormatter={(v) => formatLeadStatusLabel(String(v))}
                    />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip
                      labelFormatter={(v) => formatLeadStatusLabel(String(v))}
                      contentStyle={{ borderRadius: 8 }}
                    />
                    <Bar
                      dataKey="value"
                      fill="url(#statusBarGrad)"
                      radius={[10, 10, 0, 0]}
                      stroke="#eff6ff"
                      strokeWidth={1}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="border-slate-200/70 shadow-md bg-gradient-to-b from-white to-slate-50/30">
              <CardHeader>
                <CardTitle>Source mix</CardTitle>
              </CardHeader>
              <CardContent className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <defs>
                      {COLORS.map((c, i) => (
                        <linearGradient key={i} id={`pieGrad-${i}`} x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0%" stopColor={c} stopOpacity={1} />
                          <stop offset="100%" stopColor={c} stopOpacity={0.75} />
                        </linearGradient>
                      ))}
                    </defs>
                    <Pie
                      data={sourceRows}
                      dataKey="value"
                      nameKey="name"
                      outerRadius={108}
                      innerRadius={28}
                      paddingAngle={2}
                      stroke="#f8fafc"
                      strokeWidth={2}
                    >
                      {sourceRows.map((_, idx) => (
                        <Cell key={idx} fill={`url(#pieGrad-${idx % COLORS.length})`} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 8 }} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Conversion rate by source</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Share of cohort leads (created in range) that ever reached converted status.
                </p>
              </CardHeader>
              <CardContent className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={(movementQuery.data?.conversionRateBySource || []).map((r: { source: string; ratePercent: number; converted: number; total: number }) => ({
                      name: r.source.length > 14 ? `${r.source.slice(0, 12)}…` : r.source,
                      full: r.source,
                      rate: r.ratePercent,
                      label: `${r.converted}/${r.total}`,
                    }))}
                    layout="vertical"
                    margin={{ left: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" domain={[0, 100]} unit="%" />
                    <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 10 }} />
                    <Tooltip
                      formatter={(v: number) => [`${v}%`, "Conversion rate"]}
                      labelFormatter={(_, p) => (p?.[0]?.payload?.full as string) ?? ""}
                    />
                    <Bar dataKey="rate" fill="#059669" radius={[0, 4, 4, 0]} name="Rate %" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Median days to convert by source</CardTitle>
                <p className="text-sm text-muted-foreground">Among converted leads in the cohort only.</p>
              </CardHeader>
              <CardContent className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={(movementQuery.data?.conversionTimeBySource || [])
                      .filter((r: { count: number }) => r.count > 0)
                      .map((r: { source: string; medianDays: number | null; count: number }) => ({
                        name: r.source.length > 14 ? `${r.source.slice(0, 12)}…` : r.source,
                        full: r.source,
                        days: r.medianDays != null ? Math.round(r.medianDays * 10) / 10 : 0,
                        n: r.count,
                      }))}
                    layout="vertical"
                    margin={{ left: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 10 }} />
                    <Tooltip
                      formatter={(v: number, _name, item) => {
                        const n = (item as { payload?: { n?: number } })?.payload?.n ?? 0;
                        return [`${v} days (${n} leads)`, "Median"];
                      }}
                      labelFormatter={(_, p) =>
                        String((p?.[0] as { payload?: { full?: string } })?.payload?.full ?? "")
                      }
                    />
                    <Bar dataKey="days" fill="#7c3aed" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="border-slate-200/70 shadow-md bg-gradient-to-b from-white to-slate-50/30">
              <CardHeader>
                <CardTitle>AI priority mix</CardTitle>
                <p className="text-sm text-muted-foreground">Distribution of priority labels in the cohort.</p>
              </CardHeader>
              <CardContent className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={priorityRows} maxBarSize={72} barCategoryGap="22%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ borderRadius: 8 }} />
                    <Bar dataKey="value" radius={[10, 10, 0, 0]} stroke="#fff" strokeWidth={0.5}>
                      {priorityRows.map((row: { name: string }) => (
                        <Cell key={row.name} fill={priorityBarColor(row.name)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Leads by owner</CardTitle>
                <p className="text-sm text-muted-foreground">Top assignees in the cohort (up to 12).</p>
              </CardHeader>
              <CardContent className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={ownerRows} layout="vertical" margin={{ left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#0891b2" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Transition volume per day</CardTitle>
              <p className="text-sm text-muted-foreground">
                Count of status-change events per day (full days in range; zero when no changes).
              </p>
            </CardHeader>
            <CardContent className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={byDayRows}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 9 }} interval="preserveStartEnd" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Line type="monotone" dataKey="value" stroke="#9333ea" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Lead source counts (Day / Week / Month)</CardTitle>
              <p className="text-sm text-muted-foreground">
                Counts of leads created in the range, pivoted by acquisition source.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="text-sm font-medium mb-2">Last 7 days (day-on-day)</h3>
                <div className="overflow-x-auto border rounded-md">
                  <table className="min-w-max text-xs w-full">
                    <thead className="bg-gray-50 text-muted-foreground">
                      <tr>
                        <th className="p-2 text-left whitespace-nowrap">Source</th>
                        {timeBuckets.last7Keys.map((d) => (
                          <th key={d} className="p-2 text-center whitespace-nowrap">
                            {d.slice(5)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(timeBuckets.topSources || []).map((src: string) => (
                        <tr key={src} className="border-t">
                          <td className="p-2 whitespace-nowrap max-w-[220px] truncate" title={src}>
                            {src}
                          </td>
                          {timeBuckets.last7Keys.map((d) => (
                            <td key={d} className="p-2 text-center">
                              {timeBuckets.byDaySource?.[d]?.[src] ?? 0}
                            </td>
                          ))}
                        </tr>
                      ))}
                      {timeBuckets.otherSources?.length > 0 && (
                        <tr className="border-t bg-white">
                          <td className="p-2 whitespace-nowrap">Other sources</td>
                          {timeBuckets.last7Keys.map((d) => {
                            const topSum = (timeBuckets.topSources || []).reduce(
                              (s: number, src: string) => s + (timeBuckets.byDaySource?.[d]?.[src] ?? 0),
                              0
                            );
                            const other = (timeBuckets.dayTotals?.[d] ?? 0) - topSum;
                            return (
                              <td key={d} className="p-2 text-center">
                                {other > 0 ? other : 0}
                              </td>
                            );
                          })}
                        </tr>
                      )}
                      {timeBuckets.topSources?.length === 0 && (
                        <tr>
                          <td className="p-4 text-muted-foreground" colSpan={timeBuckets.last7Keys.length + 1}>
                            No leads in this period.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium mb-2">Week-on-week</h3>
                {timeBuckets.weekKeys.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No full weeks in the selected range.</p>
                ) : (
                  <div className="overflow-x-auto border rounded-md">
                    <table className="min-w-max text-xs w-full">
                      <thead className="bg-gray-50 text-muted-foreground">
                        <tr>
                          <th className="p-2 text-left whitespace-nowrap">Source</th>
                          {timeBuckets.weekKeys.map((wk) => (
                            <th key={wk} className="p-2 text-center whitespace-nowrap">
                              {wk.slice(5)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(timeBuckets.topSources || []).map((src: string) => (
                          <tr key={src} className="border-t">
                            <td className="p-2 whitespace-nowrap max-w-[220px] truncate" title={src}>
                              {src}
                            </td>
                            {timeBuckets.weekKeys.map((wk) => {
                              const days = timeBuckets.weekToDays?.[wk] ?? [];
                              const cnt = days.reduce(
                                (s: number, d: string) => s + (timeBuckets.byDaySource?.[d]?.[src] ?? 0),
                                0
                              );
                              return (
                                <td key={wk} className="p-2 text-center">
                                  {cnt}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                        {timeBuckets.otherSources?.length > 0 && (
                          <tr className="border-t bg-white">
                            <td className="p-2 whitespace-nowrap">Other sources</td>
                            {timeBuckets.weekKeys.map((wk) => {
                              const days = timeBuckets.weekToDays?.[wk] ?? [];
                              const bucketTotal = days.reduce(
                                (s: number, d: string) => s + (timeBuckets.dayTotals?.[d] ?? 0),
                                0
                              );
                              const topTotal = days.reduce(
                                (s: number, d: string) =>
                                  s +
                                  (timeBuckets.topSources || []).reduce(
                                    (ss: number, src: string) => ss + (timeBuckets.byDaySource?.[d]?.[src] ?? 0),
                                    0
                                  ),
                                0
                              );
                              const other = bucketTotal - topTotal;
                              return (
                                <td key={wk} className="p-2 text-center">
                                  {other > 0 ? other : 0}
                                </td>
                              );
                            })}
                          </tr>
                        )}
                        {timeBuckets.topSources?.length === 0 && (
                          <tr>
                            <td className="p-4 text-muted-foreground" colSpan={timeBuckets.weekKeys.length + 1}>
                              No leads in this period.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-sm font-medium mb-2">Month-on-month</h3>
                {timeBuckets.monthKeys.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No months in the selected range.</p>
                ) : (
                  <div className="overflow-x-auto border rounded-md">
                    <table className="min-w-max text-xs w-full">
                      <thead className="bg-gray-50 text-muted-foreground">
                        <tr>
                          <th className="p-2 text-left whitespace-nowrap">Source</th>
                          {timeBuckets.monthKeys.map((mk) => (
                            <th key={mk} className="p-2 text-center whitespace-nowrap">
                              {mk}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(timeBuckets.topSources || []).map((src: string) => (
                          <tr key={src} className="border-t">
                            <td className="p-2 whitespace-nowrap max-w-[220px] truncate" title={src}>
                              {src}
                            </td>
                            {timeBuckets.monthKeys.map((mk) => {
                              const days = timeBuckets.monthToDays?.[mk] ?? [];
                              const cnt = days.reduce(
                                (s: number, d: string) => s + (timeBuckets.byDaySource?.[d]?.[src] ?? 0),
                                0
                              );
                              return (
                                <td key={mk} className="p-2 text-center">
                                  {cnt}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                        {timeBuckets.otherSources?.length > 0 && (
                          <tr className="border-t bg-white">
                            <td className="p-2 whitespace-nowrap">Other sources</td>
                            {timeBuckets.monthKeys.map((mk) => {
                              const days = timeBuckets.monthToDays?.[mk] ?? [];
                              const bucketTotal = days.reduce(
                                (s: number, d: string) => s + (timeBuckets.dayTotals?.[d] ?? 0),
                                0
                              );
                              const topTotal = days.reduce(
                                (s: number, d: string) =>
                                  s +
                                  (timeBuckets.topSources || []).reduce(
                                    (ss: number, src: string) => ss + (timeBuckets.byDaySource?.[d]?.[src] ?? 0),
                                    0
                                  ),
                                0
                              );
                              const other = bucketTotal - topTotal;
                              return (
                                <td key={mk} className="p-2 text-center">
                                  {other > 0 ? other : 0}
                                </td>
                              );
                            })}
                          </tr>
                        )}
                        {timeBuckets.topSources?.length === 0 && (
                          <tr>
                            <td className="p-4 text-muted-foreground" colSpan={timeBuckets.monthKeys.length + 1}>
                              No leads in this period.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Lead status counts (Day / Week / Month)</CardTitle>
              <p className="text-sm text-muted-foreground">Counts of leads created in the range by current status.</p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="text-sm font-medium mb-2">Last 7 days (day-on-day)</h3>
                <div className="overflow-x-auto border rounded-md">
                  <table className="min-w-max text-xs w-full">
                    <thead className="bg-gray-50 text-muted-foreground">
                      <tr>
                        <th className="p-2 text-left whitespace-nowrap">Status</th>
                        {timeBuckets.last7Keys.map((d) => (
                          <th key={d} className="p-2 text-center whitespace-nowrap">
                            {d.slice(5)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {LEAD_STATUS_OPTIONS.map((st) => (
                        <tr key={st} className="border-t">
                          <td className="p-2 whitespace-nowrap">{formatLeadStatusLabel(st)}</td>
                          {timeBuckets.last7Keys.map((d) => (
                            <td key={d} className="p-2 text-center">
                              {timeBuckets.byDayStatus?.[d]?.[st] ?? 0}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium mb-2">Week-on-week</h3>
                {timeBuckets.weekKeys.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No full weeks in the selected range.</p>
                ) : (
                  <div className="overflow-x-auto border rounded-md">
                    <table className="min-w-max text-xs w-full">
                      <thead className="bg-gray-50 text-muted-foreground">
                        <tr>
                          <th className="p-2 text-left whitespace-nowrap">Status</th>
                          {timeBuckets.weekKeys.map((wk) => (
                            <th key={wk} className="p-2 text-center whitespace-nowrap">
                              {wk.slice(5)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {LEAD_STATUS_OPTIONS.map((st) => (
                          <tr key={st} className="border-t">
                            <td className="p-2 whitespace-nowrap">{formatLeadStatusLabel(st)}</td>
                            {timeBuckets.weekKeys.map((wk) => {
                              const days = timeBuckets.weekToDays?.[wk] ?? [];
                              const cnt = days.reduce(
                                (s: number, d: string) => s + (timeBuckets.byDayStatus?.[d]?.[st] ?? 0),
                                0
                              );
                              return (
                                <td key={wk} className="p-2 text-center">
                                  {cnt}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-sm font-medium mb-2">Month-on-month</h3>
                {timeBuckets.monthKeys.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No months in the selected range.</p>
                ) : (
                  <div className="overflow-x-auto border rounded-md">
                    <table className="min-w-max text-xs w-full">
                      <thead className="bg-gray-50 text-muted-foreground">
                        <tr>
                          <th className="p-2 text-left whitespace-nowrap">Status</th>
                          {timeBuckets.monthKeys.map((mk) => (
                            <th key={mk} className="p-2 text-center whitespace-nowrap">
                              {mk}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {LEAD_STATUS_OPTIONS.map((st) => (
                          <tr key={st} className="border-t">
                            <td className="p-2 whitespace-nowrap">{formatLeadStatusLabel(st)}</td>
                            {timeBuckets.monthKeys.map((mk) => {
                              const days = timeBuckets.monthToDays?.[mk] ?? [];
                              const cnt = days.reduce(
                                (s: number, d: string) => s + (timeBuckets.byDayStatus?.[d]?.[st] ?? 0),
                                0
                              );
                              return (
                                <td key={mk} className="p-2 text-center">
                                  {cnt}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Lead sub-status counts (nested under status)</CardTitle>
              <p className="text-sm text-muted-foreground">
                Shows status totals first, then sub-status counts under each status.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="text-sm font-medium mb-2">Last 7 days (day-on-day)</h3>
                <div className="overflow-x-auto border rounded-md">
                  <table className="min-w-max text-xs w-full">
                    <thead className="bg-gray-50 text-muted-foreground">
                      <tr>
                        <th className="p-2 text-left whitespace-nowrap">Status / Sub-status</th>
                        {timeBuckets.last7Keys.map((d) => (
                          <th key={d} className="p-2 text-center whitespace-nowrap">
                            {d.slice(5)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {LEAD_STATUS_OPTIONS.flatMap((st) => {
                        const subs = Array.from(
                          new Set(
                            (timeBuckets.topSubStatuses || [])
                              .filter((sub) => statusKeyFromSubStatus(sub) === st)
                          )
                        );
                        const hasAny =
                          timeBuckets.last7Keys.some((d) => (timeBuckets.byDayStatus?.[d]?.[st] ?? 0) > 0) ||
                          subs.length > 0;
                        if (!hasAny) return [];

                        const statusRow = (
                          <tr key={`st-${st}`} className="border-t bg-white">
                            <td className="p-2 font-medium whitespace-nowrap">{formatLeadStatusLabel(st)}</td>
                            {timeBuckets.last7Keys.map((d) => (
                              <td key={d} className="p-2 text-center font-medium">
                                {timeBuckets.byDayStatus?.[d]?.[st] ?? 0}
                              </td>
                            ))}
                          </tr>
                        );

                        const subRows = subs.map((sub) => (
                          <tr key={`sub-${st}-${sub}`} className="border-t">
                            <td className="p-2 whitespace-nowrap max-w-[260px] truncate pl-6" title={subStatusTableLabel(sub)}>
                              {subStatusTableLabel(sub)}
                            </td>
                            {timeBuckets.last7Keys.map((d) => (
                              <td key={d} className="p-2 text-center">
                                {timeBuckets.byDaySubStatusByStatus?.[d]?.[st]?.[sub] ?? 0}
                              </td>
                            ))}
                          </tr>
                        ));

                        return [statusRow, ...subRows];
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium mb-2">Week-on-week</h3>
                {timeBuckets.weekKeys.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No full weeks in the selected range.</p>
                ) : (
                  <div className="overflow-x-auto border rounded-md">
                    <table className="min-w-max text-xs w-full">
                      <thead className="bg-gray-50 text-muted-foreground">
                        <tr>
                          <th className="p-2 text-left whitespace-nowrap">Status / Sub-status</th>
                          {timeBuckets.weekKeys.map((wk) => (
                            <th key={wk} className="p-2 text-center whitespace-nowrap">
                              {wk.slice(5)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {LEAD_STATUS_OPTIONS.flatMap((st) => {
                          const subs = Array.from(
                            new Set((timeBuckets.topSubStatuses || []).filter((sub) => statusKeyFromSubStatus(sub) === st))
                          );
                          const hasAny =
                            timeBuckets.weekKeys.some((wk) => {
                              const days = timeBuckets.weekToDays?.[wk] ?? [];
                              return days.reduce((s: number, d: string) => s + (timeBuckets.byDayStatus?.[d]?.[st] ?? 0), 0) > 0;
                            }) || subs.length > 0;
                          if (!hasAny) return [];

                          const statusRow = (
                            <tr key={`wk-st-${st}`} className="border-t bg-white">
                              <td className="p-2 font-medium whitespace-nowrap">{formatLeadStatusLabel(st)}</td>
                              {timeBuckets.weekKeys.map((wk) => {
                                const days = timeBuckets.weekToDays?.[wk] ?? [];
                                const cnt = days.reduce((s: number, d: string) => s + (timeBuckets.byDayStatus?.[d]?.[st] ?? 0), 0);
                                return (
                                  <td key={wk} className="p-2 text-center font-medium">
                                    {cnt}
                                  </td>
                                );
                              })}
                            </tr>
                          );

                          const subRows = subs.map((sub) => (
                            <tr key={`wk-sub-${st}-${sub}`} className="border-t">
                              <td className="p-2 whitespace-nowrap max-w-[260px] truncate pl-6" title={subStatusTableLabel(sub)}>
                                {subStatusTableLabel(sub)}
                              </td>
                              {timeBuckets.weekKeys.map((wk) => {
                                const days = timeBuckets.weekToDays?.[wk] ?? [];
                                const cnt = days.reduce(
                                  (s: number, d: string) => s + (timeBuckets.byDaySubStatusByStatus?.[d]?.[st]?.[sub] ?? 0),
                                  0
                                );
                                return (
                                  <td key={wk} className="p-2 text-center">
                                    {cnt}
                                  </td>
                                );
                              })}
                            </tr>
                          ));

                          return [statusRow, ...subRows];
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-sm font-medium mb-2">Month-on-month</h3>
                {timeBuckets.monthKeys.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No months in the selected range.</p>
                ) : (
                  <div className="overflow-x-auto border rounded-md">
                    <table className="min-w-max text-xs w-full">
                      <thead className="bg-gray-50 text-muted-foreground">
                        <tr>
                          <th className="p-2 text-left whitespace-nowrap">Status / Sub-status</th>
                          {timeBuckets.monthKeys.map((mk) => (
                            <th key={mk} className="p-2 text-center whitespace-nowrap">
                              {mk}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {LEAD_STATUS_OPTIONS.flatMap((st) => {
                          const subs = Array.from(
                            new Set((timeBuckets.topSubStatuses || []).filter((sub) => statusKeyFromSubStatus(sub) === st))
                          );
                          const hasAny =
                            timeBuckets.monthKeys.some((mk) => {
                              const days = timeBuckets.monthToDays?.[mk] ?? [];
                              return days.reduce((s: number, d: string) => s + (timeBuckets.byDayStatus?.[d]?.[st] ?? 0), 0) > 0;
                            }) || subs.length > 0;
                          if (!hasAny) return [];

                          const statusRow = (
                            <tr key={`mo-st-${st}`} className="border-t bg-white">
                              <td className="p-2 font-medium whitespace-nowrap">{formatLeadStatusLabel(st)}</td>
                              {timeBuckets.monthKeys.map((mk) => {
                                const days = timeBuckets.monthToDays?.[mk] ?? [];
                                const cnt = days.reduce((s: number, d: string) => s + (timeBuckets.byDayStatus?.[d]?.[st] ?? 0), 0);
                                return (
                                  <td key={mk} className="p-2 text-center font-medium">
                                    {cnt}
                                  </td>
                                );
                              })}
                            </tr>
                          );

                          const subRows = subs.map((sub) => (
                            <tr key={`mo-sub-${st}-${sub}`} className="border-t">
                              <td className="p-2 whitespace-nowrap max-w-[260px] truncate pl-6" title={subStatusTableLabel(sub)}>
                                {subStatusTableLabel(sub)}
                              </td>
                              {timeBuckets.monthKeys.map((mk) => {
                                const days = timeBuckets.monthToDays?.[mk] ?? [];
                                const cnt = days.reduce(
                                  (s: number, d: string) => s + (timeBuckets.byDaySubStatusByStatus?.[d]?.[st]?.[sub] ?? 0),
                                  0
                                );
                                return (
                                  <td key={mk} className="p-2 text-center">
                                    {cnt}
                                  </td>
                                );
                              })}
                            </tr>
                          ));

                          return [statusRow, ...subRows];
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {tab === "transitions" && (
        <div className="grid grid-cols-1 gap-4">
          <Card>
            <CardHeader>
              <CardTitle>Status changes (from → to)</CardTitle>
              <p className="text-sm text-muted-foreground font-normal">
                Counts use status history events in the selected date range. Download CSV lists each
                lead transition (one row per event) for that edge.
              </p>
            </CardHeader>
            <CardContent className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={transitionEdgeRows} layout="vertical" margin={{ left: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#0891b2" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Download by transition</CardTitle>
            </CardHeader>
            <CardContent>
              {transitionQuery.isLoading ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
              ) : transitionEdgeRows.length === 0 ? (
                <p className="text-sm text-muted-foreground">No status changes in this range.</p>
              ) : (
                <div className="overflow-x-auto border rounded-md">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-muted-foreground">
                      <tr>
                        <th className="p-2 text-left font-medium">Transition</th>
                        <th className="p-2 text-right font-medium w-24">Count</th>
                        <th className="p-2 text-right font-medium w-36">CSV</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...transitionEdgeRows]
                        .sort((a, b) => b.value - a.value)
                        .map((row) => (
                          <tr key={row.name} className="border-t">
                            <td className="p-2 font-mono text-xs">{row.name}</td>
                            <td className="p-2 text-right tabular-nums">{row.value}</td>
                            <td className="p-2 text-right">
                              {!crmViewOnly && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="h-8 gap-1"
                                  disabled={
                                    downloadingTransitionEdge === row.name || row.value === 0
                                  }
                                  onClick={() => downloadTransitionCsv(row.name)}
                                >
                                  <Download className="h-3.5 w-3.5" />
                                  {downloadingTransitionEdge === row.name ? "…" : "Download"}
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {tab === "opd-outcomes" && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle>OPD visited outcomes (by source)</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Starts from leads with an OPD visit in the selected date range (diagnosed OPD appointment or
                    paid OPD bill). Shows where each lead is now: still at OPD Visited, Lost, Closed with
                    long-term follow-up, or moved to IPD (admitted / procedure / surgery case).
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadOpdOutcomeCsv()}
                  disabled={crmViewOnly}
                  className={crmViewOnly ? 'hidden' : undefined}
                >
                  Download CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {opdOutcomeQuery.isLoading ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                    <div className="rounded-md border p-3">
                      <div className="text-muted-foreground">Total OPD visited</div>
                      <div className="text-xl font-semibold">{opdOutcomeQuery.data?.totals?.total ?? 0}</div>
                    </div>
                    <div className="rounded-md border p-3">
                      <div className="text-muted-foreground">OPD Visited (pending)</div>
                      <div className="text-xl font-semibold">
                        {opdOutcomeQuery.data?.totals?.opdVisitedPending ?? 0}
                      </div>
                    </div>
                    <div className="rounded-md border p-3">
                      <div className="text-muted-foreground">Lost</div>
                      <div className="text-xl font-semibold">{opdOutcomeQuery.data?.totals?.lost ?? 0}</div>
                    </div>
                    <div className="rounded-md border p-3">
                      <div className="text-muted-foreground">Closed (LTFU)</div>
                      <div className="text-xl font-semibold">
                        {opdOutcomeQuery.data?.totals?.closedLongTermFollowUp ?? 0}
                      </div>
                    </div>
                    <div className="rounded-md border p-3">
                      <div className="text-muted-foreground">IPD</div>
                      <div className="text-xl font-semibold">{opdOutcomeQuery.data?.totals?.ipd ?? 0}</div>
                    </div>
                  </div>

                  <div className="overflow-x-auto border rounded-md">
                    <table className="min-w-max text-xs w-full">
                      <thead className="bg-gray-50 text-muted-foreground">
                        <tr>
                          <th className="p-2 text-left">Source</th>
                          <th className="p-2 text-center">OPD Visited (pending)</th>
                          <th className="p-2 text-center">Lost</th>
                          <th className="p-2 text-center">Closed (LTFU)</th>
                          <th className="p-2 text-center">IPD</th>
                          <th className="p-2 text-center font-medium">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(opdOutcomeQuery.data?.bySource ?? []).length === 0 ? (
                          <tr>
                            <td colSpan={6} className="p-4 text-center text-muted-foreground">
                              No OPD visited leads in this period.
                            </td>
                          </tr>
                        ) : (
                          <>
                            {(opdOutcomeQuery.data?.bySource ?? []).map((row: any) => (
                              <tr key={row.source} className="border-t">
                                <td className="p-2 font-medium">{row.source}</td>
                                <td className="p-2 text-center">{row.opdVisitedPending}</td>
                                <td className="p-2 text-center">{row.lost}</td>
                                <td className="p-2 text-center">{row.closedLongTermFollowUp}</td>
                                <td className="p-2 text-center">{row.ipd}</td>
                                <td className="p-2 text-center font-medium">{row.total}</td>
                              </tr>
                            ))}
                            <tr className="border-t bg-gray-50 font-medium">
                              <td className="p-2">Total</td>
                              <td className="p-2 text-center">
                                {opdOutcomeQuery.data?.totals?.opdVisitedPending ?? 0}
                              </td>
                              <td className="p-2 text-center">{opdOutcomeQuery.data?.totals?.lost ?? 0}</td>
                              <td className="p-2 text-center">
                                {opdOutcomeQuery.data?.totals?.closedLongTermFollowUp ?? 0}
                              </td>
                              <td className="p-2 text-center">{opdOutcomeQuery.data?.totals?.ipd ?? 0}</td>
                              <td className="p-2 text-center">{opdOutcomeQuery.data?.totals?.total ?? 0}</td>
                            </tr>
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Lead detail</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto border rounded-md max-h-[480px] overflow-y-auto">
                <table className="min-w-max text-xs w-full">
                  <thead className="bg-gray-50 text-muted-foreground sticky top-0">
                    <tr>
                      <th className="p-2 text-left">Patient</th>
                      <th className="p-2 text-left">Phone</th>
                      <th className="p-2 text-left">Source</th>
                      <th className="p-2 text-left">OPD visited</th>
                      <th className="p-2 text-left">Outcome</th>
                      <th className="p-2 text-left">Status</th>
                      <th className="p-2 text-left">Surgery</th>
                      <th className="p-2 text-left">Assigned</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(opdOutcomeQuery.data?.leads ?? []).length === 0 ? (
                      <tr>
                        <td colSpan={8} className="p-4 text-center text-muted-foreground">
                          No rows
                        </td>
                      </tr>
                    ) : (
                      (opdOutcomeQuery.data?.leads ?? []).map((lead: any) => (
                        <tr key={lead.leadId} className="border-t">
                          <td className="p-2">{lead.patientName}</td>
                          <td className="p-2">{lead.phone}</td>
                          <td className="p-2">{lead.source}</td>
                          <td className="p-2 whitespace-nowrap">
                            {lead.opdVisitedAt ? lead.opdVisitedAt.slice(0, 10) : "—"}
                          </td>
                          <td className="p-2">{formatOpdOutcomeLabel(lead.outcome)}</td>
                          <td className="p-2">
                            {formatLeadStatusLabel(lead.status)}
                            {lead.leadSubStatus
                              ? ` · ${formatLeadSubStatusLabel(lead.leadSubStatus)}`
                              : ""}
                          </td>
                          <td className="p-2">
                            {lead.isSurgeryCase === true
                              ? `Yes${lead.tentativeSurgeryDate ? ` · ${lead.tentativeSurgeryDate.slice(0, 10)}` : ""}`
                              : lead.isSurgeryCase === false
                                ? "No"
                                : "—"}
                          </td>
                          <td className="p-2">{lead.assignedToName || "—"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {tab === "lost" && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Lost reason categories (AI + rules)</CardTitle>
            </CardHeader>
            <CardContent className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={lostRows}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#dc2626" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Lost leads detail</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="p-2">Patient</th>
                    <th className="p-2">Owner</th>
                    <th className="p-2">Category</th>
                    <th className="p-2">Summary</th>
                    <th className="p-2">Confidence</th>
                    <th className="p-2">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {(lostQuery.data?.items || []).slice(0, 50).map((row: any) => (
                    <tr key={row.id} className="border-b">
                      <td className="p-2 font-medium">{row.patientName}</td>
                      <td className="p-2">{row.assignedTo?.name ?? "—"}</td>
                      <td className="p-2">{row.aiLostReasonCategory ?? "—"}</td>
                      <td className="p-2 max-w-xs truncate" title={row.aiLostReasonSummary}>
                        {row.aiLostReasonSummary ?? "—"}
                      </td>
                      <td className="p-2">{row.aiConfidence ?? "—"}</td>
                      <td className="p-2 whitespace-nowrap">
                        {row.updatedAt ? new Date(row.updatedAt).toLocaleDateString("en-IN") : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(lostQuery.data?.items || []).length > 50 && (
                <p className="text-xs text-muted-foreground mt-2">
                  Showing 50 of {(lostQuery.data?.items || []).length}. Use CSV export for full list.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {tab === "self-booked" && (
        <SelfBookedLeadsTab from={from} to={to} hospitalId={reportHospitalId} />
      )}

      {tab === "performance" && (
        <LeadPerformanceTab
          from={from}
          to={to}
          onFromChange={setFrom}
          onToChange={setTo}
          hospitalId={reportHospitalId}
        />
      )}

      {tab === "campaign-recommendations" && (
        <CampaignRecommendationTab hospitalId={reportHospitalId} />
      )}

      {tab === "bant-scoring" &&
        (user?.role === "HOSPITAL_ADMIN" || user?.role === "SUPER_ADMIN") && (
          <LeadBantScoringTab
            from={from}
            to={to}
            onFromChange={setFrom}
            onToChange={setTo}
            hospitalId={reportHospitalId}
          />
        )}

      {tab === "talk-time" && (
        <LeadTalkTimeTab
          from={from}
          to={to}
          onFromChange={setFrom}
          onToChange={setTo}
          hospitalId={reportHospitalId}
        />
      )}

      {tab === "frt" && (
        <LeadFrtTab from={from} to={to} hospitalId={reportHospitalId} />
      )}

      {tab === "ageing" && (
        <LeadOpenAgeingTab from={from} to={to} hospitalId={reportHospitalId} />
      )}

      {tab === "opd-report" && canSeeOpdReport && (
        <LeadOpdReportTab from={from} to={to} hospitalId={reportHospitalId} />
      )}

      {tab === "sarvam-opd-performance" && canSeeOpdReport && (
        <LeadSarvamOpdPerformanceTab from={from} to={to} hospitalId={reportHospitalId} />
      )}

      {tab === "organic-conversions" && canSeeOpdReport && (
        <OrganicLeadConversionsTab from={from} to={to} hospitalId={reportHospitalId} />
      )}

      {tab === "benchmark" && user?.role === "SUPER_ADMIN" && (
        <Card>
          <CardHeader>
            <CardTitle>Hospital comparison (leads created in range)</CardTitle>
          </CardHeader>
          <CardContent className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={benchmarkRows}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={70} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="total" name="Total" fill="#2563eb" />
                <Bar dataKey="converted" name="Converted" fill="#16a34a" />
                <Bar dataKey="lost" name="Lost" fill="#dc2626" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
